import { NonNullish } from "./nullish-check";
import formatMoney from "./format-money";

interface SellingPlanGroup {
  app_id: string;
  id: string;
  name: string;
  options: {
    name: string;
    position: number;
    values: string[];
  }[];
  selling_plans: {
    id: number;
    description: string;
    name: string;
    recurring_deliveries: boolean;
  }[];
}

type LoopGetChildVariants = {
  isBundleProduct: boolean;
  mapping: {
    [key: string]: {
      allowCheckout: boolean;
      childProducts: {
        childVariantId: number;
        inventoryManagement: string
        inventoryPolicy: string
        inventoryQuantity: string
        quantity: number;
      }[]
      outOfStock: boolean;
    }
  },
  purchaseType: string;
  loopBundleId: string;
  loopBundleName: string;
  bundleId: number;
  productVariantMapping: {
    [key: string]: number
  },
  productVariantDiscountMapping: {
    [key: string]: {
      purchaseType: string;
      bundleDiscountId: number;
      bundleDiscountType: string;
      bundleDiscountValue: number;
      bundleDiscountComputedValue: number;
      spbpDiscount: {
        [key: string]: number
      }
    }[]
  };
  productVariantSellingPlanMapping: {
    [key: string]: number
  };
  productVariantsFinalValues: {
    [key: string]: {
      [key: string]: number;
      ONETIME: number
    }
  };
  "showBundleName": boolean
}

export async function checkSellingPlans(
  data: { selling_plan_groups: SellingPlanGroup[]; id: number; url: string },
  node: HTMLElement
) {
  const spgs =
    data.selling_plan_groups?.flatMap((spg) =>
      spg.selling_plans.map((sp) => ({
        selling_plan_group_id: spg.id,
        selling_plan_id: sp.id,
      }))
    ) || [];
  const STORAGE_KEY = `bundle-data-${data.id}`;

  let bundleDataJSON: LoopGetChildVariants = sessionStorage.getItem(STORAGE_KEY) ? JSON.parse(sessionStorage.getItem(STORAGE_KEY)!) : null;

  if (!sessionStorage.getItem(STORAGE_KEY)) {
    const bundleData = await fetch(`https://apiv2.loopwork.co/bundleStorefront/getChildVariants?id=${data.id}`);
    bundleDataJSON = (await bundleData.json() as { data: LoopGetChildVariants }).data;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bundleDataJSON));
  }

  if (bundleDataJSON.isBundleProduct) {
    Object.keys(bundleDataJSON.productVariantMapping).forEach(variantId => {
      const variantGroup = node.querySelector(`[data-variant-id="${variantId}"]`);
      const variantPrices = bundleDataJSON.productVariantsFinalValues[variantId];

      if (variantGroup && variantPrices) {
        Object.keys(variantPrices).forEach(sellingPlanId => {
          const price = variantPrices[sellingPlanId]! * 100;
          let sellingPlanElm = variantGroup.querySelector(`option[value="${sellingPlanId}"]`)?.closest('.selling-plan-widget');
          if (sellingPlanId === "ONETIME") {
            sellingPlanElm = variantGroup.querySelector(`.selling-plan-widget[data-selling-plan-group="one-time"]`)
          }

          if (!sellingPlanElm) return;

          const priceElm: HTMLElement = NonNullish(sellingPlanElm.querySelector('[data-selling-plan-price]'));
          const comparePriceElm: HTMLElement = NonNullish(sellingPlanElm.querySelector('[data-selling-plan-compare-price]'));

          comparePriceElm.hidden = priceElm.textContent?.trim() === formatMoney(price).toString();
          comparePriceElm.textContent = priceElm.textContent;
          priceElm.textContent = formatMoney(price).toString();
        })
      }
    })
  } else {
    let sellingPlans;
    const sps =
      data.selling_plan_groups?.flatMap((spg) =>
        spg?.selling_plans.map((sp) => sp.id)
      ) || [];
    const HIDDEN_PLANS_STORAGE_KEY = `sps-${data.id}-${sps.join('-')}`;

    if (sessionStorage.getItem(HIDDEN_PLANS_STORAGE_KEY)) {
      sellingPlans = JSON.parse(sessionStorage.getItem(HIDDEN_PLANS_STORAGE_KEY)!);
    } else {
      const body = {
        domain: window.Shopify.shop,
        sellingPlanIds: sps,
        product_shopify_id: data.id,
      };

      const bundleRes = await fetch(
        'https://apiv2.loopwork.co/bundleStorefront/filterBundleSellingPlanIds',
        {
          method: 'POST',
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const bundleResJSON = await bundleRes.json();
      sellingPlans = bundleResJSON.data;

      sessionStorage.setItem(HIDDEN_PLANS_STORAGE_KEY, JSON.stringify(sellingPlans));
    }

    for (const spg of spgs) {
      if (
        sellingPlans.includes(spg.selling_plan_id) ||
        sellingPlans.includes(spg.selling_plan_id.toString())
      ) {
        const elem = node.querySelector(
          `[data-selling-plan-group="${spg.selling_plan_group_id}"]`
        );

        if (elem) {
          elem.remove();
        }
      }
    }
  }
}
